import { api } from './api';
import type { GSTR1Report, GSTR3BReport, ReportingGSTIN } from '../types/gst-report.types';

export type InvoiceStatus = 'draft' | 'issued' | 'paid' | 'cancelled' | 'overdue';
export type InvoiceType = 'tax_invoice' | 'bill_of_supply' | 'export_invoice' | 'debit_note' | 'credit_note';
export type GSTType = 'cgst_sgst' | 'igst' | 'exempt' | 'zero_rated';

export interface InvoiceItem {
  id: string;
  lineNumber?: number;
  productId: string;
  productName: string;
  hsnCode: string;
  quantity: number;
  unit: string;
  rate: number;
  // Percent of quantity x rate
  discount: number;
  taxableAmount: number;
  cgstRate: number;
  cgstAmount: number;
  sgstRate: number;
  sgstAmount: number;
  igstRate: number;
  igstAmount: number;
  cessRate: number;
  cessAmount: number;
  totalAmount: number;
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  invoiceType: InvoiceType;
  invoiceDate: string;
  dueDate: string;
  // "overdue" is not stored: an issued invoice past its due date is overdue
  status: InvoiceStatus;
  customerId: string;
  customerName: string;
  customerAddress: string;
  // Null for an unregistered customer
  customerGSTIN?: string | null;
  customerState: string;
  customerStateCode: string;
  salesOrderId?: string;
  supplierName: string;
  supplierAddress: string;
  supplierGSTIN: string;
  supplierState: string;
  supplierStateCode: string;
  supplierPAN?: string;
  placeOfSupply: string;
  placeOfSupplyCode: string;
  gstType: GSTType;
  totalTaxableAmount: number;
  totalCGST: number;
  totalSGST: number;
  totalIGST: number;
  totalCess: number;
  totalDiscount: number;
  roundOff: number;
  grandTotal: number;
  amountInWords: string;
  eInvoiceNumber?: string;
  eInvoiceDate?: string;
  eInvoiceQRCode?: string;
  eInvoiceIRN?: string;
  eInvoiceAckNumber?: string;
  eInvoiceAckDate?: string;
  transportMode?: string;
  vehicleNumber?: string;
  transporterName?: string;
  transporterId?: string;
  transporterDocNumber?: string;
  transporterDocDate?: string;
  eWayBillNumber?: string;
  eWayBillDate?: string;
  eWayBillValidUpto?: string;
  bankName?: string;
  bankAccountNumber?: string;
  bankIFSC?: string;
  bankBranch?: string;
  notes?: string;
  termsAndConditions?: string;
  reverseCharge: boolean;
  // Loaded with a single invoice; list rows carry only the count
  items?: InvoiceItem[];
  itemCount?: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy?: string;
}

export interface InvoiceItemInput {
  productId: string;
  productName?: string;
  hsnCode: string;
  quantity: number;
  unit?: string;
  rate: number;
  // Percent of quantity x rate
  discount?: number;
  // Total GST rate; the server splits it into CGST and SGST or charges it as IGST
  gstRate?: number;
  cessRate?: number;
}

export interface CreateInvoiceDto {
  invoiceType?: InvoiceType;
  invoiceDate: string;
  dueDate: string;
  customerId: string;
  salesOrderId?: string;
  supplierName?: string;
  supplierAddress?: string;
  supplierGSTIN?: string;
  supplierPAN?: string;
  // GST state code; the server names the state and decides the tax type from it
  placeOfSupplyCode?: string;
  bankName?: string | null;
  bankAccountNumber?: string | null;
  bankIFSC?: string | null;
  bankBranch?: string | null;
  notes?: string | null;
  termsAndConditions?: string | null;
  reverseCharge?: boolean;
  items: InvoiceItemInput[];
}

export type UpdateInvoiceDto = Partial<CreateInvoiceDto>;

export interface InvoiceQueryParams {
  page: number;
  limit: number;
  search?: string;
  status?: InvoiceStatus;
  invoiceType?: InvoiceType;
  dateFrom?: string;
  dateTo?: string;
  // Only invoices raised under this seller GST number
  supplierGSTIN?: string;
  // Only invoices that go into the GST returns (issued or paid)
  reportable?: boolean;
}

export interface PaginatedInvoices {
  data: Invoice[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface InvoiceSummary {
  total: number;
  draft: number;
  // Issued and not yet due
  issued: number;
  // Issued and past the due date
  overdue: number;
  paid: number;
  cancelled: number;
  // Value of every issued invoice that is not paid yet, overdue ones included
  outstandingAmount: number;
  overdueAmount: number;
  paidAmount: number;
}

// Seller details a new invoice starts with
export interface InvoiceSeller {
  // Where the details come from: the company settings, the primary company, the latest invoice, or nowhere yet
  source: 'settings' | 'company' | 'last_invoice' | 'none';
  name: string;
  address: string;
  gstin: string;
  pan: string;
  state: string;
  stateCode: string;
  bankName: string;
  bankAccountNumber: string;
  bankIFSC: string;
  bankBranch: string;
  // What a new invoice starts with; set in the company settings
  paymentTermDays?: number;
  termsAndConditions?: string;
  notes?: string;
}

export interface HSNCode {
  id: string;
  code: string;
  description: string;
  cgstRate: number;
  sgstRate: number;
  igstRate: number;
  cessRate: number;
}

export class InvoiceService {
  static async getAllInvoices(): Promise<Invoice[]> {
    const response = await api.get('/gst/invoices');
    return response.data;
  }

  static async getInvoicesPage(query: InvoiceQueryParams): Promise<PaginatedInvoices> {
    const params = {
      page: query.page,
      limit: query.limit,
      ...(query.search ? { search: query.search } : {}),
      ...(query.status ? { status: query.status } : {}),
      ...(query.invoiceType ? { invoiceType: query.invoiceType } : {}),
      ...(query.dateFrom ? { dateFrom: query.dateFrom } : {}),
      ...(query.dateTo ? { dateTo: query.dateTo } : {}),
      ...(query.supplierGSTIN ? { supplierGSTIN: query.supplierGSTIN } : {}),
      ...(query.reportable ? { reportable: true } : {}),
    };
    const response = await api.get<PaginatedInvoices>('/gst/invoices/paginated', { params });
    return response.data;
  }

  static async getInvoiceSummary(): Promise<InvoiceSummary> {
    const response = await api.get<InvoiceSummary>('/gst/invoices/summary');
    return response.data;
  }

  static async getSeller(): Promise<InvoiceSeller> {
    const response = await api.get<InvoiceSeller>('/gst/invoices/seller');
    return response.data;
  }

  static async getHSNCodes(): Promise<HSNCode[]> {
    const response = await api.get<HSNCode[]>('/gst/hsn-codes');
    // Rates are stored as decimals, which arrive as text
    return response.data.map((hsn) => ({
      ...hsn,
      cgstRate: Number(hsn.cgstRate),
      sgstRate: Number(hsn.sgstRate),
      igstRate: Number(hsn.igstRate),
      cessRate: Number(hsn.cessRate),
    }));
  }

  // Issue an invoice, mark it paid or cancel it
  static async updateInvoiceStatus(id: string, status: InvoiceStatus): Promise<Invoice> {
    const response = await api.patch(`/gst/invoices/${id}/status`, { status });
    return response.data;
  }

  static async getInvoiceById(id: string): Promise<Invoice> {
    const response = await api.get(`/gst/invoices/${id}`);
    return response.data;
  }

  static async createInvoice(data: CreateInvoiceDto): Promise<Invoice> {
    const response = await api.post('/gst/invoices', data);
    return response.data;
  }

  static async updateInvoice(id: string, data: UpdateInvoiceDto): Promise<Invoice> {
    const response = await api.patch(`/gst/invoices/${id}`, data);
    return response.data;
  }

  static async deleteInvoice(id: string): Promise<void> {
    await api.delete(`/gst/invoices/${id}`);
  }

  static async downloadInvoicePDF(id: string): Promise<Blob> {
    const response = await api.get(`/gst/invoices/${id}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  }

  static async generateEInvoice(id: string): Promise<any> {
    const response = await api.post(`/gst/invoices/${id}/einvoice`);
    return response.data;
  }

  // GST numbers that invoices have been raised under
  static async getReportingGSTINs(): Promise<ReportingGSTIN[]> {
    const response = await api.get<ReportingGSTIN[]>('/gst/reports/gstins');
    return response.data;
  }

  static async generateGSTR1Report(gstin: string, fromDate: string, toDate: string): Promise<GSTR1Report> {
    const response = await api.get<GSTR1Report>('/gst/reports/gstr1', {
      params: { gstin, fromDate, toDate },
    });
    return response.data;
  }

  static async generateGSTR3BReport(gstin: string, fromDate: string, toDate: string): Promise<GSTR3BReport> {
    const response = await api.get<GSTR3BReport>('/gst/reports/gstr3b', {
      params: { gstin, fromDate, toDate },
    });
    return response.data;
  }

  static async exportGSTR1Report(gstin: string, fromDate: string, toDate: string): Promise<Blob> {
    const response = await api.get('/gst/reports/gstr1/export', {
      params: { gstin, fromDate, toDate },
      responseType: 'blob',
    });
    return response.data;
  }

  static async exportGSTR3BReport(gstin: string, fromDate: string, toDate: string): Promise<Blob> {
    const response = await api.get('/gst/reports/gstr3b/export', {
      params: { gstin, fromDate, toDate },
      responseType: 'blob',
    });
    return response.data;
  }
}